/**
 * MarketplaceSearch.jsx
 * 
 * Search results page for the marketplace.
 * Features:
 * - Category pills at top for quick filtering
 * - Clean search header with logo
 * - Experience cards with custom shadows
 * - Filters for city, category, sorting
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SocialLinksLight } from '../components/SocialLinks';
import useSWR from 'swr';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  MapPin, 
  Star, 
  Clock, 
  Filter,
  X,
  Globe,
  SlidersHorizontal,
  Loader2, 
  ChevronRight,
  Compass,
  User,
  Package
} from 'lucide-react';
import { getCategoryMeta } from '../constants/categories';
import { GoogleReviewBadge, ExperienceCard, PackageCard } from '../components/marketplace';
import { getCityImage } from '../constants/cityImages';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';
import CountrySelect from '../components/CountrySelect';
import ItineraryButton from '../components/ItineraryButton';
import {
  getMarketplaceCountry,
  setMarketplaceCountry,
  captureMarketplaceAttribution,
} from '../utils/marketplaceLocale';
import { initMetaPixel, trackMarketplaceSearch } from '../utils/analytics';
import { bfTrack } from '../utils/bfWidgetTrack';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SWR fetcher
const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
};

// Debounce hook for search input
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
};

// Skeleton loader for experience cards
const ExperienceCardSkeleton = () => (
  <Card className="rounded-3xl border-0 bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
    <div className="aspect-[4/3] rounded-2xl bg-gray-100 animate-pulse" />
    <CardContent className="px-1 pt-3 pb-1">
      <div className="flex items-center justify-between gap-2">
        <div className="h-4 w-3/4 rounded bg-gray-100 animate-pulse" />
        <div className="h-4 w-8 rounded bg-gray-100 animate-pulse" />
      </div>
      <div className="mt-2 h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
      <div className="mt-3 flex items-center justify-between">
        <div className="h-4 w-16 rounded bg-gray-100 animate-pulse" />
        <div className="h-3 w-10 rounded bg-gray-100 animate-pulse" />
      </div>
    </CardContent>
  </Card>
);

const MarketplaceSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = React.useRef(null);
  
  // URL params
  const cityParam = searchParams.get('city') || '';
  const categoryParam = searchParams.get('category') || '';
  const queryParam = searchParams.get('q') || '';
  // Ad weighting param — ?company=slug1,slug2 boosts those operators in ranking (never filters)
  const companyParam = searchParams.get('company') || '';
  
  // State - only for controlled inputs
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [selectedCity, setSelectedCity] = useState(cityParam);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [offset, setOffset] = useState(0);
  const [country, setCountry] = useState(getMarketplaceCountry);
  // Per-visit rotation seed — new order every page load, stable across pagination
  const [visitSeed] = useState(() => Math.random().toString(36).slice(2, 10));
  const limit = 100;

  // Capture ad attribution (utm params) + fire marketplace beacon once on mount
  useEffect(() => {
    captureMarketplaceAttribution();
    initMetaPixel();
    bfTrack('bf-marketplace', 'page_view', {
      page: 'search',
      country: getMarketplaceCountry(),
      city: cityParam || undefined,
      category: categoryParam || undefined,
      utm: Object.fromEntries(
        ['source', 'medium', 'campaign'].map((k) => [k, searchParams.get(`utm_${k}`)]).filter(([, v]) => v)
      ),
    });
  }, []); // eslint-disable-line

  const handleCountryChange = (code) => {
    setCountry(code);
    setMarketplaceCountry(code);
    setSelectedCity('');
    setOffset(0);
    const params = new URLSearchParams(searchParams);
    params.set('country', code.toLowerCase());
    params.delete('city');
    setSearchParams(params);
  };

  // Debounce search query for API calls (300ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // SWR for filter options (cities & categories) - cached aggressively, country-scoped
  const { data: citiesData } = useSWR(
    `${API_URL}/api/marketplace/cities?country=${country}`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 120000, errorRetryCount: 2 }
  );

  const { data: categoriesData } = useSWR(
    `${API_URL}/api/marketplace/categories?country=${country}`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 120000, errorRetryCount: 2 }
  );

  // 📦 Operator bundles — shown above results when browsing (no query/category)
  const { data: packagesData } = useSWR(
    `${API_URL}/api/marketplace/packages?country=${country}${selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : ''}&limit=4`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000, errorRetryCount: 2 }
  );
  const mpPackages = packagesData?.packages || [];
  const showPackagesRow = mpPackages.length > 0 && !debouncedSearchQuery && (!selectedCategory || selectedCategory === 'all');

  // Build search URL for SWR - uses debounced query
  const searchUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.append('country', country);
    if (selectedCity) params.append('city', selectedCity.slice(0, 100));
    if (selectedCategory) params.append('category', selectedCategory.slice(0, 50));
    if (debouncedSearchQuery) params.append('q', debouncedSearchQuery.slice(0, 200));
    if (companyParam) params.append('company', companyParam.slice(0, 200));
    params.append('sortBy', sortBy);
    params.append('seed', visitSeed);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return `${API_URL}/api/marketplace/search?${params.toString()}`;
  }, [country, selectedCity, selectedCategory, debouncedSearchQuery, companyParam, sortBy, offset, visitSeed]);

  // SWR for search results - revalidates when URL changes
  const { data: searchData, isLoading: loading } = useSWR(
    searchUrl,
    fetcher,
    { 
      revalidateOnFocus: false, 
      dedupingInterval: 10000,
      keepPreviousData: true, // Keep showing old data while loading new
      errorRetryCount: 2 
    }
  );

  // Derived state from SWR
  const cities = citiesData?.cities || [];
  const categories = categoriesData?.categories || [];
  const results = searchData?.services || [];
  const totalCount = searchData?.total || 0;

  // Data-driven category pills — only categories that actually have live
  // services in the selected country (no more empty-category clutter)
  const quickCategories = useMemo(() => [
    { id: 'all', name: 'All Experiences', icon: '🌊' },
    ...categories.map((c) => {
      const meta = getCategoryMeta(c.id, c.displayName);
      return { id: c.id, name: meta.name, icon: meta.icon };
    }),
  ], [categories]);

  // "More to explore" — when a category filter is active, keep surfacing other
  // bookable experiences in the same country below the results
  const { data: moreData } = useSWR(
    selectedCategory && selectedCategory !== 'all'
      ? `${API_URL}/api/marketplace/search?country=${country}&sortBy=popular&limit=8`
      : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000, errorRetryCount: 1 }
  );
  const moreExperiences = useMemo(() => {
    const shownIds = new Set(results.map((s) => s.id));
    return (moreData?.services || []).filter((s) => !shownIds.has(s.id)).slice(0, 4);
  }, [moreData, results]);

  // Set active quick filter based on category param
  useEffect(() => {
    setActiveQuickFilter(categoryParam || 'all');
  }, [categoryParam]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    
    // Update URL params
    const params = new URLSearchParams();
    params.set('country', country.toLowerCase());
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
    trackMarketplaceSearch({ query: searchQuery, city: selectedCity, category: selectedCategory, country });
  };

  const handleCityChange = (value) => {
    setSelectedCity(value === 'all' ? '' : value);
    setOffset(0);
  };

  const handleQuickFilter = (categoryId) => {
    setActiveQuickFilter(categoryId);
    if (categoryId === 'all') {
      setSelectedCategory('');
    } else {
      setSelectedCategory(categoryId);
    }
    setOffset(0);
  };

  const clearFilters = () => {
    setSelectedCity('');
    setSelectedCategory('');
    setSearchQuery('');
    setActiveQuickFilter('all');
    setOffset(0);
    setSearchParams({});
  };

  const handleExperienceClick = (service) => {
    navigate(`/experience/${service.id}${searchParams.get('ref') ? `?ref=${searchParams.get('ref')}` : ''}`);
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  const hasActiveFilters = selectedCity || selectedCategory || searchQuery;

  const prettyCategory = (slug) => {
    const cat = quickCategories.find(c => c.id === slug);
    if (cat?.name) return cat.name;
    return String(slug).replace(/[-_]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  const getSearchTitle = () => {
    if (selectedCity && selectedCategory && selectedCategory !== 'all') {
      return `${prettyCategory(selectedCategory)} in ${selectedCity}`;
    }
    if (selectedCity) return `${selectedCity} Experiences`;
    if (selectedCategory && selectedCategory !== 'all') {
      return prettyCategory(selectedCategory);
    }
    if (searchQuery) return `Results for "${searchQuery}"`;
    return 'All Experiences';
  };

  // Filter cities based on search input for dropdown
  const filteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <Link to="/marketplace" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-9 w-9 object-contain rounded-lg"
              />
              <span className="text-xl font-bold text-gray-900 hidden sm:block">BookingFlows</span>
            </Link>

            {/* Search Bar with Dropdown */}
            <div ref={searchRef} className="flex-1 max-w-xl hidden md:block relative">
              <form onSubmit={handleSearch}>
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-full border border-gray-200">
                  <div className="flex-1 flex items-center gap-2 pl-3">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value.slice(0, 100));
                        setShowSearchDropdown(true);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      placeholder="Where to?"
                      maxLength={100}
                      className="flex-1 border-0 outline-none text-sm placeholder:text-gray-400 bg-transparent"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="hidden sm:block px-1 border-l border-gray-200 pl-2">
                    <CountrySelect value={country} onChange={handleCountryChange} compact />
                  </div>
                  <Button 
                    type="submit"
                    size="sm"
                    className="rounded-full px-4 text-xs bg-blue-600 hover:bg-blue-700"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* City Dropdown */}
              {showSearchDropdown && (filteredCities.length > 0 || cities.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-3xl overflow-hidden shadow-xl ring-1 ring-gray-100 z-[100]">
                  <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {searchQuery ? 'Matching Cities' : 'Popular Destinations'}
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {(searchQuery ? filteredCities : cities).slice(0, 5).map((city) => (
                      <button
                        key={city.city}
                        onClick={() => {
                          setShowSearchDropdown(false);
                          setSelectedCity(city.city);
                          setOffset(0);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={getCityImage(city.city)}
                            alt={city.city}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{city.city}</p>
                          <p className="text-xs text-gray-500">{city.country}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                        </div>
                      </button>
                    ))}
                    {filteredCities.length === 0 && searchQuery && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No destinations found for "{searchQuery}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to="/for-suppliers" className="text-sm text-gray-600 hover:text-gray-900 hidden md:block">
                List Your Business
              </Link>
              {isAuthenticated ? (
                <Link to="/my-trips">
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                    {customer?.avatar_url ? (
                      <img 
                        src={customer.avatar_url} 
                        alt={customer.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium text-gray-700 pr-1">
                      {customer?.name?.split(' ')[0] || 'My Trips'}
                    </span>
                  </div>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowSignInModal(true)}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Category Pills */}
      <div className="bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {/* "All Experiences" always first */}
            <button
              onClick={() => handleQuickFilter('all')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all duration-200 ${
                activeQuickFilter === 'all'
                  ? 'bg-[#04091B] text-white'
                  : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
              }`}
              data-testid="quick-filter-all"
            >
              <span className="text-base">🌊</span>
              <span>All Experiences</span>
            </button>
            {/* Selected category right after "All", with X to clear */}
            {activeQuickFilter && activeQuickFilter !== 'all' && (
              <button
                onClick={() => handleQuickFilter('all')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 bg-[#04091B] text-white transition-colors duration-200"
                data-testid="selected-category"
              >
                <span className="text-base">{getCategoryMeta(activeQuickFilter, quickCategories.find(c => c.id === activeQuickFilter)?.name).icon}</span>
                <span>{getCategoryMeta(activeQuickFilter, quickCategories.find(c => c.id === activeQuickFilter)?.name).name}</span>
                <X className="w-4 h-4 ml-1 hover:text-gray-300" />
              </button>
            )}
            {/* Remaining categories */}
            {quickCategories
              .filter(cat => cat.id !== activeQuickFilter && cat.id !== 'all')
              .map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleQuickFilter(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all duration-200 ${
                    cat.id === 'all' && activeQuickFilter === 'all'
                      ? 'bg-[#04091B] text-white'
                      : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`quick-filter-${cat.id}`}
                >
                  <span className="text-base">{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Header with Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{getSearchTitle()}</h1>
            <p className="text-gray-500 mt-1">
              {loading ? 'Searching...' : `${totalCount} ${totalCount === 1 ? 'experience' : 'experiences'}${showPackagesRow ? ` · ${mpPackages.length} ${mpPackages.length === 1 ? 'package' : 'packages'}` : ''} available`}
            </p>
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-3">
            <CountrySelect value={country} onChange={handleCountryChange} />
            <Select value={selectedCity || 'all'} onValueChange={handleCityChange}>
              <SelectTrigger className="w-[160px] h-10 rounded-full bg-white border-gray-200 shadow-sm hover:shadow transition-shadow" data-testid="city-filter">
                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Any city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any city</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.city} value={city.city}>
                    {city.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[170px] h-10 rounded-full bg-white border-gray-200 shadow-sm hover:shadow transition-shadow" data-testid="sort-filter">
                <SlidersHorizontal className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-gray-600 hover:text-gray-900 rounded-full border-gray-200 hover:border-gray-300"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* 📦 Bundle & save — packages row (browse mode only) */}
        {showPackagesRow && (
          <div className="mb-10" data-testid="search-packages-section">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Bundle &amp; save with packages</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {mpPackages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} testId={`search-package-card-${pkg.id}`} />
              ))}
            </div>
          </div>
        )}

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <ExperienceCardSkeleton key={i} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No experiences found</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              We couldn&apos;t find any experiences matching your criteria. Try adjusting your filters or exploring a different category.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={clearFilters} variant="outline" className="rounded-full">
                Clear all filters
              </Button>
              <Link to="/marketplace">
                <Button className="rounded-full bg-gray-900 hover:bg-gray-800">
                  Explore destinations
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {results.map((service) => (
                <ExperienceCard
                  key={service.id}
                  service={service}
                  featured={Boolean(service.operator?.featured)}
                  testId={`result-card-${service.id}`}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalCount > limit && (
              <div className="flex items-center justify-center gap-4 mt-12">
                <Button
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="rounded-full"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {Math.floor(offset / limit) + 1} of {Math.ceil(totalCount / limit)}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= totalCount}
                  className="rounded-full"
                >
                  Next
                </Button>
              </div>
            )}
            {/* You might also like — keep pushing bookable inventory */}
            {selectedCategory && selectedCategory !== 'all' && moreExperiences.length > 0 && (
              <div className="mt-14" data-testid="more-experiences-section">
                <h2 className="text-lg md:text-lg font-semibold text-gray-900 mb-4">
                  More to explore in {country === 'UAE' ? 'the UAE' : 'the USA'}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {moreExperiences.map((service) => (
                    <ExperienceCard key={service.id} service={service} testId={`more-experience-${service.id}`} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 bg-white border-t border-gray-100 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-lg"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <Link to="/marketplace" className="hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
              <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <div className="flex flex-col items-center gap-3">
              <SocialLinksLight />
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} BookingFlows
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Customer Sign In Modal */}
      <CustomerSignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal} 
      />

      {/* Bottom fade effect for smooth scrolling */}
      <div 
        className="fixed bottom-0 left-0 right-0 h-16 pointer-events-none z-40"
        style={{
          background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      <ItineraryButton />
    </div>
  );
};

export default MarketplaceSearch;
